import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
    Shield, Target, BookOpen, Search, ChevronRight, ChevronDown,
    AlertTriangle, Clock, FileText, ArrowLeft, Brain, Database,
    Layers, Activity, Filter, ExternalLink, Copy, CheckCircle
} from 'lucide-react';
import { mitreAPI, playbooksAPI, intelligenceAPI } from '@/lib/api';
import { LOGO_URL } from '@/lib/brand';

const Intelligence = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('overview');
    const [isLoading, setIsLoading] = useState(true);
    const [summary, setSummary] = useState(null);
    
    // MITRE state
    const [tactics, setTactics] = useState([]);
    const [techniques, setTechniques] = useState([]);
    const [selectedTactic, setSelectedTactic] = useState(null);
    const [selectedTechnique, setSelectedTechnique] = useState(null);
    const [techniqueSearch, setTechniqueSearch] = useState('');
    
    // Playbooks state
    const [playbooks, setPlaybooks] = useState([]);
    const [playbookCategories, setPlaybookCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedPlaybook, setSelectedPlaybook] = useState(null);
    const [expandedSteps, setExpandedSteps] = useState({});

    useEffect(() => {
        const token = localStorage.getItem('shirosec_token');
        if (!token) {
            navigate('/login');
            return;
        }
        loadInitialData();
    }, [navigate]);

    const loadInitialData = async () => {
        setIsLoading(true);
        try {
            const [summaryRes, tacticsRes, techniquesRes, playbooksRes, categoriesRes] = await Promise.all([
                intelligenceAPI.getSummary(),
                mitreAPI.listTactics(),
                mitreAPI.listTechniques(),
                playbooksAPI.list(),
                playbooksAPI.getCategories()
            ]);
            setSummary(summaryRes.data);
            setTactics(tacticsRes.data);
            setTechniques(techniquesRes.data.techniques || []);
            setPlaybooks(playbooksRes.data.playbooks || []);
            setPlaybookCategories(categoriesRes.data.categories || []);
        } catch (error) {
            console.error('Error loading intelligence data:', error);
            toast.error('Failed to load intelligence data');
        } finally {
            setIsLoading(false);
        }
    };

    const loadTechniqueDetails = async (techniqueId) => {
        try {
            const response = await mitreAPI.getTechnique(techniqueId);
            setSelectedTechnique(response.data);
        } catch (error) {
            console.error('Error loading technique:', error);
            toast.error('Failed to load technique details');
        }
    };

    const loadPlaybookDetails = async (playbookId) => {
        try {
            const response = await playbooksAPI.get(playbookId);
            setSelectedPlaybook(response.data);
            setExpandedSteps({});
        } catch (error) {
            console.error('Error loading playbook:', error);
            toast.error('Failed to load playbook details');
        }
    };

    const filterTechniquesByTactic = async (tactic) => {
        setSelectedTactic(tactic);
        setSelectedTechnique(null);
        try {
            const response = await mitreAPI.listTechniques(tactic);
            setTechniques(response.data.techniques || []);
        } catch (error) {
            console.error('Error filtering techniques:', error);
        }
    };

    const filterPlaybooksByCategory = async (category) => {
        setSelectedCategory(category);
        setSelectedPlaybook(null);
        try {
            const response = await playbooksAPI.list(category);
            setPlaybooks(response.data.playbooks || []);
        } catch (error) {
            console.error('Error filtering playbooks:', error);
        }
    };

    const toggleStep = (stepOrder) => {
        setExpandedSteps(prev => ({
            ...prev,
            [stepOrder]: !prev[stepOrder]
        }));
    };

    const copyToClipboard = (text, label) => {
        navigator.clipboard.writeText(text);
        toast.success(`${label} copied to clipboard`);
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
            case 'high': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
            case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
            case 'low': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
            default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
        }
    };

    const getTacticColor = (tactic) => {
        const colors = {
            'Reconnaissance': 'from-gray-500 to-gray-600',
            'Resource Development': 'from-slate-500 to-slate-600',
            'Initial Access': 'from-red-500 to-red-600',
            'Execution': 'from-orange-500 to-orange-600',
            'Persistence': 'from-yellow-500 to-yellow-600',
            'Privilege Escalation': 'from-lime-500 to-lime-600',
            'Defense Evasion': 'from-green-500 to-green-600',
            'Credential Access': 'from-emerald-500 to-emerald-600',
            'Discovery': 'from-teal-500 to-teal-600',
            'Lateral Movement': 'from-cyan-500 to-cyan-600',
            'Collection': 'from-sky-500 to-sky-600',
            'Command and Control': 'from-blue-500 to-blue-600',
            'Exfiltration': 'from-indigo-500 to-indigo-600',
            'Impact': 'from-purple-500 to-purple-600'
        };
        return colors[tactic] || 'from-gray-500 to-gray-600';
    };

    const filteredTechniques = techniques.filter(t => 
        t.name.toLowerCase().includes(techniqueSearch.toLowerCase()) ||
        t.id.toLowerCase().includes(techniqueSearch.toLowerCase())
    );

    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
                <div className="text-center">
                    <Brain className="w-12 h-12 text-cyan-400 animate-pulse mx-auto mb-4" />
                    <p className="text-white/60">Loading security intelligence...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a0a0f] text-white">
            {/* Header */}
            <header className="sticky top-0 z-30 bg-[#0a0a0f]/90 backdrop-blur-lg border-b border-white/5 px-6 py-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/dashboard" className="p-2 hover:bg-white/5 rounded-lg transition-colors">
                            <ArrowLeft className="w-5 h-5 text-white/60" />
                        </Link>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center">
                                <Brain className="w-5 h-5 text-cyan-400" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold">Security Intelligence</h1>
                                <p className="text-sm text-white/40">MITRE ATT&CK & Threat Hunting Playbooks</p>
                            </div>
                        </div>
                    </div>
                    <Link to="/dashboard">
                        <img src={LOGO_URL} alt="Shiro" className="w-8 h-8 object-contain opacity-60 hover:opacity-100 transition-opacity" />
                    </Link>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto p-6">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                    <TabsList className="bg-[#0d0d14] border border-white/5 p-1">
                        <TabsTrigger 
                            value="overview" 
                            className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400"
                            data-testid="tab-overview"
                        >
                            <Database className="w-4 h-4 mr-2" />
                            Overview
                        </TabsTrigger>
                        <TabsTrigger 
                            value="mitre" 
                            className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400"
                            data-testid="tab-mitre"
                        >
                            <Target className="w-4 h-4 mr-2" />
                            MITRE ATT&CK
                        </TabsTrigger>
                        <TabsTrigger 
                            value="playbooks" 
                            className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400"
                            data-testid="tab-playbooks"
                        >
                            <BookOpen className="w-4 h-4 mr-2" />
                            Playbooks
                        </TabsTrigger>
                    </TabsList>

                    {/* Overview Tab */}
                    <TabsContent value="overview" className="space-y-6">
                        {/* Stats Cards */}
                        <div className="grid md:grid-cols-3 gap-6">
                            <Card className="bg-[#0d0d14] border-white/5">
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-500/20 to-orange-500/20 flex items-center justify-center">
                                            <Target className="w-7 h-7 text-red-400" />
                                        </div>
                                        <div>
                                            <div className="text-3xl font-bold">{summary?.mitre?.total_techniques || 0}</div>
                                            <div className="text-sm text-white/60">MITRE Techniques</div>
                                            <div className="text-xs text-white/40">+{summary?.mitre?.total_sub_techniques || 0} sub-techniques</div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="bg-[#0d0d14] border-white/5">
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
                                            <Layers className="w-7 h-7 text-cyan-400" />
                                        </div>
                                        <div>
                                            <div className="text-3xl font-bold">{summary?.mitre?.total_tactics || 0}</div>
                                            <div className="text-sm text-white/60">Attack Tactics</div>
                                            <div className="text-xs text-white/40">Kill chain stages</div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="bg-[#0d0d14] border-white/5">
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-4">
                                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                                            <BookOpen className="w-7 h-7 text-purple-400" />
                                        </div>
                                        <div>
                                            <div className="text-3xl font-bold">{summary?.playbooks?.total || 0}</div>
                                            <div className="text-sm text-white/60">Hunting Playbooks</div>
                                            <div className="text-xs text-white/40">{playbookCategories.length} categories</div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Kill Chain Overview */}
                        <Card className="bg-[#0d0d14] border-white/5">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Activity className="w-5 h-5 text-cyan-400" />
                                    MITRE ATT&CK Kill Chain
                                </CardTitle>
                                <CardDescription>Click on a tactic to explore related techniques</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
                                    {tactics.map((tactic) => (
                                        <button
                                            key={tactic.id}
                                            onClick={() => { filterTechniquesByTactic(tactic.name); setActiveTab('mitre'); }}
                                            className={`p-3 rounded-lg bg-gradient-to-br ${getTacticColor(tactic.name)} hover:scale-105 transition-transform text-left`}
                                            data-testid={`tactic-${tactic.id}`}
                                        >
                                            <div className="text-xs font-bold text-white/90 mb-1">{tactic.id}</div>
                                            <div className="text-[11px] text-white/80 font-medium leading-tight">{tactic.name}</div>
                                        </button>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Quick Access Playbooks */}
                        <Card className="bg-[#0d0d14] border-white/5">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <AlertTriangle className="w-5 h-5 text-orange-400" />
                                    Critical Response Playbooks
                                </CardTitle>
                                <CardDescription>Quick access to high-priority incident response guides</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {playbooks.filter(p => p.severity === 'critical' || p.severity === 'high').slice(0, 6).map((playbook) => (
                                        <button
                                            key={playbook.id}
                                            onClick={() => { loadPlaybookDetails(playbook.id); setActiveTab('playbooks'); }}
                                            className="p-4 bg-white/5 hover:bg-white/10 rounded-xl text-left transition-colors group"
                                            data-testid={`playbook-quick-${playbook.id}`}
                                        >
                                            <div className="flex items-start justify-between mb-2">
                                                <Badge className={getSeverityColor(playbook.severity)}>
                                                    {playbook.severity}
                                                </Badge>
                                                <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-cyan-400 transition-colors" />
                                            </div>
                                            <h4 className="font-semibold text-white mb-1">{playbook.name}</h4>
                                            <p className="text-xs text-white/50 line-clamp-2">{playbook.description}</p>
                                            <div className="flex items-center gap-2 mt-2 text-xs text-white/40">
                                                <Clock className="w-3 h-3" />
                                                {playbook.estimated_time}
                                                <span className="mx-1">•</span>
                                                {playbook.step_count} steps
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* MITRE ATT&CK Tab */}
                    <TabsContent value="mitre" className="space-y-6">
                        <div className="grid lg:grid-cols-4 gap-6">
                            {/* Tactics Sidebar */}
                            <div className="lg:col-span-1 space-y-4">
                                <Card className="bg-[#0d0d14] border-white/5">
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-base flex items-center gap-2">
                                            <Layers className="w-4 h-4 text-cyan-400" />
                                            Tactics
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-1 max-h-[60vh] overflow-y-auto">
                                        <button
                                            onClick={() => { setSelectedTactic(null); loadInitialData(); }}
                                            className={`w-full p-2 rounded-lg text-left text-sm transition-colors ${!selectedTactic ? 'bg-cyan-500/20 text-cyan-400' : 'hover:bg-white/5 text-white/70'}`}
                                        >
                                            All Tactics
                                        </button>
                                        {tactics.map((tactic) => (
                                            <button
                                                key={tactic.id}
                                                onClick={() => filterTechniquesByTactic(tactic.name)}
                                                className={`w-full p-2 rounded-lg text-left text-sm transition-colors ${selectedTactic === tactic.name ? 'bg-cyan-500/20 text-cyan-400' : 'hover:bg-white/5 text-white/70'}`}
                                            >
                                                <span className="font-mono text-xs text-white/40 mr-2">{tactic.id}</span>
                                                {tactic.name}
                                            </button>
                                        ))}
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Techniques List */}
                            <div className="lg:col-span-3 space-y-4">
                                {/* Search */}
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                                    <Input
                                        placeholder="Search techniques by name or ID..."
                                        value={techniqueSearch}
                                        onChange={(e) => setTechniqueSearch(e.target.value)}
                                        className="pl-10 bg-[#0d0d14] border-white/10 text-white placeholder:text-white/40"
                                        data-testid="technique-search"
                                    />
                                </div>

                                {selectedTechnique ? (
                                    /* Technique Detail View */
                                    <Card className="bg-[#0d0d14] border-white/5">
                                        <CardHeader>
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Badge variant="outline" className="font-mono text-cyan-400 border-cyan-500/30">
                                                            {selectedTechnique.id}
                                                        </Badge>
                                                        <Badge className="bg-white/10 text-white/70">
                                                            {selectedTechnique.tactic}
                                                        </Badge>
                                                    </div>
                                                    <CardTitle className="text-xl">{selectedTechnique.name}</CardTitle>
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setSelectedTechnique(null)}
                                                    className="text-white/60 hover:text-white"
                                                >
                                                    <ArrowLeft className="w-4 h-4 mr-1" /> Back
                                                </Button>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="space-y-6">
                                            <div>
                                                <h4 className="text-sm font-semibold text-white/60 mb-2">Description</h4>
                                                <p className="text-white/80">{selectedTechnique.description}</p>
                                            </div>

                                            {selectedTechnique.detection && (
                                                <div>
                                                    <h4 className="text-sm font-semibold text-white/60 mb-2">Detection Methods</h4>
                                                    <div className="flex flex-wrap gap-2">
                                                        {selectedTechnique.detection.map((method, i) => (
                                                            <Badge key={i} variant="outline" className="bg-green-500/10 text-green-400 border-green-500/30">
                                                                {method}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {selectedTechnique.mitigation && (
                                                <div>
                                                    <h4 className="text-sm font-semibold text-white/60 mb-2">Mitigations</h4>
                                                    <div className="flex flex-wrap gap-2">
                                                        {selectedTechnique.mitigation.map((mit, i) => (
                                                            <Badge key={i} variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30">
                                                                {mit}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {selectedTechnique.sub_techniques && Object.keys(selectedTechnique.sub_techniques).length > 0 && (
                                                <div>
                                                    <h4 className="text-sm font-semibold text-white/60 mb-2">Sub-Techniques ({Object.keys(selectedTechnique.sub_techniques).length})</h4>
                                                    <div className="grid md:grid-cols-2 gap-2">
                                                        {Object.entries(selectedTechnique.sub_techniques).map(([id, sub]) => (
                                                            <div key={id} className="p-3 bg-white/5 rounded-lg">
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <span className="font-mono text-xs text-cyan-400">{id}</span>
                                                                </div>
                                                                <div className="text-sm font-medium text-white/90">{sub.name}</div>
                                                                <div className="text-xs text-white/50 mt-1">{sub.description}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="flex items-center gap-2 pt-4 border-t border-white/10">
                                                <a 
                                                    href={`https://attack.mitre.org/techniques/${selectedTechnique.id}/`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                                                >
                                                    View on MITRE ATT&CK <ExternalLink className="w-3 h-3" />
                                                </a>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ) : (
                                    /* Techniques Grid */
                                    <div className="grid md:grid-cols-2 gap-3">
                                        {filteredTechniques.map((technique) => (
                                            <button
                                                key={technique.id}
                                                onClick={() => loadTechniqueDetails(technique.id)}
                                                className="p-4 bg-[#0d0d14] border border-white/5 rounded-xl text-left hover:border-cyan-500/30 transition-colors group"
                                                data-testid={`technique-${technique.id}`}
                                            >
                                                <div className="flex items-start justify-between mb-2">
                                                    <Badge variant="outline" className="font-mono text-cyan-400 border-cyan-500/30 text-xs">
                                                        {technique.id}
                                                    </Badge>
                                                    {technique.has_sub_techniques && (
                                                        <Badge className="bg-purple-500/20 text-purple-400 text-xs">
                                                            +{technique.sub_technique_count} sub
                                                        </Badge>
                                                    )}
                                                </div>
                                                <h4 className="font-semibold text-white group-hover:text-cyan-400 transition-colors mb-1">
                                                    {technique.name}
                                                </h4>
                                                <p className="text-xs text-white/50 line-clamp-2">{technique.description}</p>
                                                <div className="flex items-center gap-2 mt-2">
                                                    <Badge className="bg-white/5 text-white/50 text-xs">
                                                        {technique.tactic}
                                                    </Badge>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {filteredTechniques.length === 0 && !selectedTechnique && (
                                    <div className="text-center py-12 text-white/40">
                                        <Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                        <p>No techniques found matching your search</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </TabsContent>

                    {/* Playbooks Tab */}
                    <TabsContent value="playbooks" className="space-y-6">
                        <div className="grid lg:grid-cols-4 gap-6">
                            {/* Categories Sidebar */}
                            <div className="lg:col-span-1 space-y-4">
                                <Card className="bg-[#0d0d14] border-white/5">
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-base flex items-center gap-2">
                                            <Filter className="w-4 h-4 text-purple-400" />
                                            Categories
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-1">
                                        <button
                                            onClick={() => { setSelectedCategory(null); loadInitialData(); setSelectedPlaybook(null); }}
                                            className={`w-full p-2 rounded-lg text-left text-sm transition-colors flex items-center justify-between ${!selectedCategory ? 'bg-purple-500/20 text-purple-400' : 'hover:bg-white/5 text-white/70'}`}
                                        >
                                            All Playbooks
                                            <Badge variant="outline" className="text-xs">{playbooks.length}</Badge>
                                        </button>
                                        {playbookCategories.map((cat) => (
                                            <button
                                                key={cat.name}
                                                onClick={() => filterPlaybooksByCategory(cat.name)}
                                                className={`w-full p-2 rounded-lg text-left text-sm transition-colors flex items-center justify-between ${selectedCategory === cat.name ? 'bg-purple-500/20 text-purple-400' : 'hover:bg-white/5 text-white/70'}`}
                                            >
                                                {cat.name}
                                                <Badge variant="outline" className="text-xs">{cat.count}</Badge>
                                            </button>
                                        ))}
                                    </CardContent>
                                </Card>

                                {/* Severity Legend */}
                                <Card className="bg-[#0d0d14] border-white/5">
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-base">Severity Levels</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <Badge className={getSeverityColor('critical')}>Critical</Badge>
                                            <span className="text-xs text-white/50">Immediate action</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Badge className={getSeverityColor('high')}>High</Badge>
                                            <span className="text-xs text-white/50">Within hours</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Badge className={getSeverityColor('medium')}>Medium</Badge>
                                            <span className="text-xs text-white/50">Within days</span>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Playbooks List/Detail */}
                            <div className="lg:col-span-3 space-y-4">
                                {selectedPlaybook ? (
                                    /* Playbook Detail View */
                                    <Card className="bg-[#0d0d14] border-white/5">
                                        <CardHeader>
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Badge className={getSeverityColor(selectedPlaybook.severity)}>
                                                            {selectedPlaybook.severity}
                                                        </Badge>
                                                        <Badge variant="outline" className="text-white/60 border-white/20">
                                                            {selectedPlaybook.category}
                                                        </Badge>
                                                    </div>
                                                    <CardTitle className="text-xl">{selectedPlaybook.name}</CardTitle>
                                                    <CardDescription className="mt-2">{selectedPlaybook.description}</CardDescription>
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setSelectedPlaybook(null)}
                                                    className="text-white/60 hover:text-white"
                                                >
                                                    <ArrowLeft className="w-4 h-4 mr-1" /> Back
                                                </Button>
                                            </div>
                                            <div className="flex items-center gap-4 mt-4 text-sm text-white/50">
                                                <div className="flex items-center gap-1">
                                                    <Clock className="w-4 h-4" />
                                                    {selectedPlaybook.estimated_time}
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <FileText className="w-4 h-4" />
                                                    {selectedPlaybook.steps?.length || 0} steps
                                                </div>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            {/* MITRE Techniques */}
                                            {selectedPlaybook.enriched_techniques?.length > 0 && (
                                                <div className="p-4 bg-white/5 rounded-xl">
                                                    <h4 className="text-sm font-semibold text-white/60 mb-3">Related MITRE ATT&CK Techniques</h4>
                                                    <div className="flex flex-wrap gap-2">
                                                        {selectedPlaybook.enriched_techniques.map((tech) => (
                                                            <button
                                                                key={tech.id}
                                                                onClick={() => { loadTechniqueDetails(tech.id); setActiveTab('mitre'); }}
                                                                className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg text-sm font-mono transition-colors"
                                                            >
                                                                {tech.id}: {tech.name}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Steps */}
                                            <div className="space-y-3">
                                                <h4 className="text-sm font-semibold text-white/60">Procedure Steps</h4>
                                                {selectedPlaybook.steps?.map((step) => (
                                                    <div key={step.order} className="border border-white/10 rounded-xl overflow-hidden">
                                                        <button
                                                            onClick={() => toggleStep(step.order)}
                                                            className="w-full p-4 flex items-center justify-between bg-white/5 hover:bg-white/10 transition-colors"
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-8 h-8 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center font-bold text-sm">
                                                                    {step.order}
                                                                </div>
                                                                <div className="text-left">
                                                                    <div className="font-semibold text-white">{step.title}</div>
                                                                    <div className="text-xs text-white/50">{step.description}</div>
                                                                </div>
                                                            </div>
                                                            {expandedSteps[step.order] ? (
                                                                <ChevronDown className="w-5 h-5 text-white/40" />
                                                            ) : (
                                                                <ChevronRight className="w-5 h-5 text-white/40" />
                                                            )}
                                                        </button>
                                                        
                                                        {expandedSteps[step.order] && (
                                                            <div className="p-4 space-y-4 border-t border-white/10">
                                                                {/* Actions */}
                                                                {step.actions?.length > 0 && (
                                                                    <div>
                                                                        <h5 className="text-xs font-semibold text-white/50 mb-2 uppercase">Actions</h5>
                                                                        <ul className="space-y-1">
                                                                            {step.actions.map((action, i) => (
                                                                                <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                                                                                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                                                                                    {action}
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                )}

                                                                {/* Commands */}
                                                                {step.commands && (
                                                                    <div>
                                                                        <h5 className="text-xs font-semibold text-white/50 mb-2 uppercase">Commands</h5>
                                                                        <div className="space-y-2">
                                                                            {Object.entries(step.commands).map(([os, cmd]) => (
                                                                                <div key={os} className="bg-black/50 rounded-lg p-3">
                                                                                    <div className="flex items-center justify-between mb-2">
                                                                                        <span className="text-xs font-semibold text-cyan-400 uppercase">{os}</span>
                                                                                        <button
                                                                                            onClick={() => copyToClipboard(cmd, `${os} command`)}
                                                                                            className="text-white/40 hover:text-white"
                                                                                        >
                                                                                            <Copy className="w-4 h-4" />
                                                                                        </button>
                                                                                    </div>
                                                                                    <pre className="text-xs text-white/70 font-mono whitespace-pre-wrap break-all">{cmd}</pre>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* Queries */}
                                                                {step.queries && (
                                                                    <div>
                                                                        <h5 className="text-xs font-semibold text-white/50 mb-2 uppercase">Detection Queries</h5>
                                                                        <div className="space-y-2">
                                                                            {Object.entries(step.queries).map(([platform, query]) => (
                                                                                <div key={platform} className="bg-black/50 rounded-lg p-3">
                                                                                    <div className="flex items-center justify-between mb-2">
                                                                                        <span className="text-xs font-semibold text-purple-400 uppercase">{platform}</span>
                                                                                        <button
                                                                                            onClick={() => copyToClipboard(query, `${platform} query`)}
                                                                                            className="text-white/40 hover:text-white"
                                                                                        >
                                                                                            <Copy className="w-4 h-4" />
                                                                                        </button>
                                                                                    </div>
                                                                                    <pre className="text-xs text-white/70 font-mono whitespace-pre-wrap break-all">{query}</pre>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* Tools */}
                                                                {step.tools?.length > 0 && (
                                                                    <div>
                                                                        <h5 className="text-xs font-semibold text-white/50 mb-2 uppercase">Recommended Tools</h5>
                                                                        <div className="flex flex-wrap gap-2">
                                                                            {step.tools.map((tool, i) => (
                                                                                <Badge key={i} variant="outline" className="bg-white/5 text-white/70">
                                                                                    {tool}
                                                                                </Badge>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* Resources */}
                                                                {step.resources?.length > 0 && (
                                                                    <div>
                                                                        <h5 className="text-xs font-semibold text-white/50 mb-2 uppercase">Resources</h5>
                                                                        <div className="flex flex-wrap gap-2">
                                                                            {step.resources.map((resource, i) => (
                                                                                <a
                                                                                    key={i}
                                                                                    href={resource.url}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="flex items-center gap-1 text-sm text-cyan-400 hover:text-cyan-300"
                                                                                >
                                                                                    {resource.name} <ExternalLink className="w-3 h-3" />
                                                                                </a>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>
                                ) : (
                                    /* Playbooks Grid */
                                    <div className="grid md:grid-cols-2 gap-4">
                                        {playbooks.map((playbook) => (
                                            <button
                                                key={playbook.id}
                                                onClick={() => loadPlaybookDetails(playbook.id)}
                                                className="p-5 bg-[#0d0d14] border border-white/5 rounded-xl text-left hover:border-purple-500/30 transition-colors group"
                                                data-testid={`playbook-${playbook.id}`}
                                            >
                                                <div className="flex items-start justify-between mb-3">
                                                    <Badge className={getSeverityColor(playbook.severity)}>
                                                        {playbook.severity}
                                                    </Badge>
                                                    <Badge variant="outline" className="text-white/50 border-white/20 text-xs">
                                                        {playbook.category}
                                                    </Badge>
                                                </div>
                                                <h4 className="font-semibold text-white group-hover:text-purple-400 transition-colors mb-2">
                                                    {playbook.name}
                                                </h4>
                                                <p className="text-sm text-white/50 line-clamp-2 mb-3">{playbook.description}</p>
                                                <div className="flex items-center gap-4 text-xs text-white/40">
                                                    <div className="flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        {playbook.estimated_time}
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <FileText className="w-3 h-3" />
                                                        {playbook.step_count} steps
                                                    </div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {playbooks.length === 0 && !selectedPlaybook && (
                                    <div className="text-center py-12 text-white/40">
                                        <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                        <p>No playbooks found in this category</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
};

export default Intelligence;
